import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronsRight } from 'lucide-react';
import { useLanguageContext } from '../context/languageContext';

/**
 * A horizontal snap-scrolling row on phones that becomes a normal grid from
 * `md` up. Carries three swipe affordances, all mobile-only:
 *   - the next card peeks past the screen edge
 *   - a fade on the right edge, which disappears once you reach the end
 *   - clickable position dots, plus a "swipe" hint that retires after first use
 *
 * `gridClass` is applied from md upward, so each caller keeps its own desktop
 * column count. `fadeClass` should match the section background.
 */
export default function SwipeRow({ gridClass, gapClass = 'gap-3 md:gap-6', fadeClass, className = '', children }) {
  const { t } = useLanguageContext();
  const ref = useRef(null);
  const [active, setActive] = useState(0);
  const [atEnd, setAtEnd] = useState(false);
  const [hasSwiped, setHasSwiped] = useState(false);

  const count = Array.isArray(children) ? children.length : 1;

  const sync = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const left = el.getBoundingClientRect().left + 16;
    let best = 0;
    let bestDist = Infinity;
    [...el.children].forEach((kid, i) => {
      const d = Math.abs(kid.getBoundingClientRect().left - left);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    });
    // At the far right the last card can never reach the left edge, so
    // "nearest to the left edge" would leave the final dot unreachable.
    const ended = el.scrollLeft >= el.scrollWidth - el.clientWidth - 2;
    setActive(ended ? el.children.length - 1 : best);
    setAtEnd(ended);
    if (el.scrollLeft > 8) setHasSwiped(true);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Called straight from the scroll event rather than rAF-throttled: with a
    // handful of cards this is a few rect reads, and it keeps the dots in sync
    // even when rAF is starved (background tab, headless renderer).
    el.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    sync();
    return () => {
      el.removeEventListener('scroll', sync);
      window.removeEventListener('resize', sync);
    };
  }, [sync]);

  // Scroll the row only — never the page.
  const goTo = (i) => {
    const el = ref.current;
    if (!el || !el.children[i]) return;
    const delta = el.children[i].getBoundingClientRect().left - el.getBoundingClientRect().left - 16;
    el.scrollBy({ left: delta, behavior: 'smooth' });
  };

  return (
    <div className={`relative ${className}`}>
      <div
        ref={ref}
        className={`flex md:grid ${gridClass} ${gapClass} overflow-x-auto md:overflow-visible snap-x snap-mandatory scroll-pl-4 md:scroll-pl-0 no-scrollbar -mx-4 px-4 md:mx-0 md:px-0`}
      >
        {children}
      </div>

      {/* Edge fade — a hint that the row continues past the screen */}
      <div
        aria-hidden="true"
        className={`md:hidden absolute right-0 top-0 bottom-0 w-14 bg-gradient-to-l ${fadeClass} to-transparent pointer-events-none transition-opacity duration-300 ${
          atEnd ? 'opacity-0' : 'opacity-100'
        }`}
      />

      {/* Dots + first-run swipe hint */}
      <div className="md:hidden flex items-center justify-center gap-3 mt-4">
        <div className="flex items-center gap-1.5">
          {Array.from({ length: count }, (_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => goTo(i)}
              aria-label={`${t('Go to slide')} ${i + 1}`}
              aria-current={i === active}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === active ? 'w-6 bg-gold-deep' : 'w-1.5 bg-gold-deep/30'
              }`}
            />
          ))}
        </div>

        <span
          aria-hidden="true"
          className={`flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-gold-deep/80 transition-opacity duration-500 ${
            hasSwiped ? 'opacity-0' : 'opacity-100'
          }`}
        >
          {t('Swipe')}
          <ChevronsRight className="w-3.5 h-3.5 animate-pulse" />
        </span>
      </div>
    </div>
  );
}
